import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { ArrowLeft, FileCode, FileDown } from "lucide-react";

import { LoadingPage } from "@/components/shared/LoadingPage";
import { PageHeader } from "@/components/shared/PageHeader";
import { RecibidaDetailStatusBanner } from "@/components/shared/RecibidaDetailStatusBanner";
import { Button } from "@/components/ui/button";
import { invoiceKeys } from "@/lib/queryKeys";
import { RecibidaDetailView } from "@/pages/invoices/recibida-detail-view";
import {
  downloadRecibidaPdf,
  downloadRecibidaXml,
  getRecibidaById,
} from "@/services/invoiceService";

export function RecibidaDetailPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const {
    data: detail,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: invoiceKeys.recibidaDetail(id ?? ""),
    queryFn: () => getRecibidaById(id!),
    enabled: Boolean(id),
  });

  useEffect(() => {
    if (isError) {
      toast.error(t("recibidas.detailLoadError"));
    }
  }, [isError, t]);

  if (!id) {
    return (
      <div className="space-y-4 fx-content">
        <p className="text-muted-foreground">{t("recibidas.detailNotFound")}</p>
        <Button asChild variant="outline" className="transition-colors duration-150">
          <Link to="/invoices/recibidas">{t("recibidas.backToList")}</Link>
        </Button>
      </div>
    );
  }

  if (isError && !isLoading) {
    return (
      <div className="space-y-4 fx-content">
        <PageHeader title={t("recibidas.detailTitle")} />
        <p className="text-muted-foreground">
          {(error as Error)?.message ?? t("recibidas.detailLoadError")}
        </p>
        <Button asChild variant="outline" className="transition-colors duration-150">
          <Link to="/invoices/recibidas">{t("recibidas.backToList")}</Link>
        </Button>
      </div>
    );
  }

  if (isLoading || !detail) {
    return <LoadingPage />;
  }

  return (
    <div className="min-h-full bg-[var(--fx-paper)] dark:bg-[var(--dk-bg)]">
      <div className="space-y-4 fx-content">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            {t("recibidas.detailTitle")}
          </h1>
          <div className="flex flex-wrap items-center gap-2 lg:shrink-0 lg:pl-6">
            <Button
              type="button"
              variant="outline"
              className="gap-1.5 transition-colors duration-150"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft className="h-4 w-4" aria-hidden />
              {t("recibidas.backToList")}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="gap-1.5 transition-colors duration-150"
              onClick={() => {
                void downloadRecibidaPdf(detail.id).catch(() => {
                  toast.error(t("recibidas.downloadPdfError"));
                });
              }}
            >
              <FileDown className="h-4 w-4" aria-hidden />
              {t("recibidas.actionDownloadPdf")}
            </Button>
            {detail.hasRawXml ? (
              <Button
                type="button"
                variant="outline"
                className="gap-1.5 transition-colors duration-150"
                onClick={() => {
                  void downloadRecibidaXml(detail.id).catch(() => {
                    toast.error(t("recibidas.downloadXmlError"));
                  });
                }}
              >
                <FileCode className="h-4 w-4" aria-hidden />
                {t("recibidas.actionViewXml")}
              </Button>
            ) : null}
          </div>
        </div>

        <RecibidaDetailStatusBanner estado={detail.estado} />

        <RecibidaDetailView detail={detail} t={t} i18nLanguage={i18n.language} />
      </div>
    </div>
  );
}
